// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import React, { ReactNode } from 'react';
import {
    AuxiliaryActions,
    FormField,
    Heading,
    Link,
    Paragraph,
    Status,
    Tooltip,
    useTranslate,
} from '@plesk/ui-library';
import { Locale } from 'jsw';
import ButtonWithConfirmation from 'components/common/ButtonWithConfirmation';

export type McpSettingsProps = {
    isDashboardConnected: boolean;
    isMcpAuthorized: boolean;
    isMcpAuthorizationAvailable: boolean;
    onConnectDashboard: () => void;
    onDisconnectDashboard: () => void;
    onAuthorizeMcp: () => void;
    onDeauthorizeMcp: () => void;
};

type RowProps = {
    label: ReactNode;
    description: ReactNode;
    isOn: boolean;
    action: ReactNode;
    onClick: () => void;
    confirmationText?: ReactNode;
    disabled?: boolean;
    tooltip?: ReactNode;
};

const Row = ({ label, description, isOn, action, onClick, confirmationText, disabled, tooltip }: RowProps) => {
    const link = confirmationText ? (
        <ButtonWithConfirmation
            target={<Link>{action}</Link>}
            confirmationText={confirmationText}
            isCritical
            onClick={onClick}
        />
    ) : (
        <Link disabled={disabled} onClick={() => onClick()}>{action}</Link>
    );

    return (
        <FormField label={label} description={description}>
            <Status compact intent={isOn ? 'success' : 'inactive'}>
                {Locale.getSection('components.elements.switch').lmsg(isOn ? 'onState' : 'offState')}
            </Status>
            {' '}
            <AuxiliaryActions>
                {tooltip ? <Tooltip title={tooltip}>{link}</Tooltip> : link}
            </AuxiliaryActions>
        </FormField>
    );
};

const McpSettings = ({
    isDashboardConnected,
    isMcpAuthorized,
    isMcpAuthorizationAvailable,
    onConnectDashboard,
    onDisconnectDashboard,
    onAuthorizeMcp,
    onDeauthorizeMcp,
}: McpSettingsProps) => {
    const translate = useTranslate('components.copilot');

    return (
        <div className="plesk-copilot-settings-mcp">
            <Heading level={3}>{translate('settings-view.mcp.title')}</Heading>
            <Paragraph>{translate('settings-view.mcp.description')}</Paragraph>
            <div>
                {isDashboardConnected ? (
                    <Row
                        label={translate('settings-view.dashboard.title')}
                        description={translate('settings-view.dashboard.description')}
                        isOn
                        action={translate('settings-view.dashboard.disconnect')}
                        confirmationText={translate('settings-view.dashboard.confirm-disconnect')}
                        onClick={onDisconnectDashboard}
                    />
                ) : (
                    <Row
                        label={translate('settings-view.dashboard.title')}
                        description={translate('settings-view.dashboard.description')}
                        isOn={false}
                        action={translate('settings-view.dashboard.connect')}
                        onClick={onConnectDashboard}
                    />
                )}
                {isMcpAuthorized ? (
                    <Row
                        label={translate('settings-view.mcp-auth.title')}
                        description={translate('settings-view.mcp-auth.description')}
                        isOn
                        action={translate('settings-view.mcp-auth.deauthorize')}
                        confirmationText={translate('settings-view.mcp-auth.confirm-deauthorize')}
                        onClick={onDeauthorizeMcp}
                    />
                ) : (
                    <Row
                        label={translate('settings-view.mcp-auth.title')}
                        description={translate('settings-view.mcp-auth.description')}
                        isOn={false}
                        action={translate('settings-view.mcp-auth.authorize')}
                        onClick={onAuthorizeMcp}
                        disabled={!isDashboardConnected || !isMcpAuthorizationAvailable}
                        tooltip={isDashboardConnected ? undefined : translate('settings-view.mcp-auth.requires-dashboard')}
                    />
                )}
            </div>
        </div>
    );
};

export default McpSettings;
